/** @jsx jsx */

import {
  Fragment as _,
  useContext,
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
} from 'react';
import { keyframes } from 'emotion';
import { css, jsx } from '@emotion/core';
import { useSelector } from 'react-redux';
import EditMarker from 'components/UI/EditMarker';
import { colors, getButtonFocus } from 'styles/variables';
import getBlockById from 'services/apiHelpers/getBlockById';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import { interactionContext } from 'components/lib/TextAndImage/interactionContext';
import { unlockInteraction } from 'services/unlockInteraction';
import Transcript from './Transcript';
import Audio_default from 'assets/Audio.png';

const AUDIO_FILE_DEFAULT =
  'https://freesound.org/data/previews/542/542450_5674468-lq.mp3';

const { lPink } = colors;

const frameSelected = keyframes`
  from {
    width: 50%;
    height: 50%;
  }
  to {
    width: 75%;
    height: 75%;
  }
`;

let audioResizeDelay;

const ShowTranscriptBtn = ({ id, setShowTranscript }) => {
  return (
    <button
      id={`show-transcript-${id}`}
      css={css`
        position: absolute;
        left: 50%;
        bottom: -25px;
        font-size: 12px;

        transform: translateX(-50%);
        border: none;
        outline: none;
        background: transparent;

        text-decoration: underline;
        cursor: pointer;

        &:focus {
          ${getButtonFocus('#2684FF')}
        }
      `}
      onClick={() => {
        setShowTranscript(true);
      }}
      aria-label="Show transcript"
    >
      Show transcript
    </button>
  );
};

const Audio = ({
  id,
  children,
}: {
  id?: any;
  children?: any;
}) => {
  const { entry, init } = useIntersectionObserver(true) as any;

  const block: any = getBlockById(id);

  const {
    audioSrc = AUDIO_FILE_DEFAULT,
    imgSrc = Audio_default,
    useTranscript,
  } = block.props;

  const [isPlaying, setIsPlaying] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);

  const focusedNodeId = useSelector((state: any) => {
    return state.focusedNodeId;
  });

  const selected = focusedNodeId === id;
  const context: any = useContext(interactionContext);
  const mounted = useRef(false);
  const audio = useRef<any>(null);
  const audioPausedByUser = useRef<boolean>(false);
  const player = useRef(null);

  let autoPlay = block.props.autoPlay || false;
  if (audioPausedByUser.current) {
    autoPlay = false;
  }

  const [transcript] = children;

  useLayoutEffect(() => {
    if (!mounted.current) {
      init([audio.current], {
        threshold: 0.5,
      });
    }

    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (window.dev && window.dev()) {
      return;
    }

    if (!audio.current) {
      return;
    }

    const onEnded = () => {
      console.log('audio ended');
      unlockInteraction(context.id);
    };

    audio.current.addEventListener('ended', onEnded);

    return () => {
      audio.current.removeEventListener('ended', onEnded);
    };
  }, []);

  // console.log(entry, isPlaying);

  if (window.dev && !window.dev() && entry) {
    if (entry.isIntersecting) {
      //
      if (autoPlay && !isPlaying) {
        //  && isPlaying
        audio.current.currentTime = 0;
        audio.current.play();
        // setIsPlaying(true);
      }
    } else {
      audio.current.pause();
      // setIsPlaying(false);
    }
  }

  return (
    <_>
      {window.dev() ? (
        <div
          css={css`
            position: relative;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            left: 0px;
            top: 0px;
            width: 100%;
            height: 100%;
          `}
        >
          <img
            src={imgSrc}
            css={css({
              // width: '85%',
            })}
          />
          {/* {img} */}
          <div
            css={css({
              width: '100%',
              // margin: '0 0 20px 0',
              padding: 20,
            })}
          >
            <audio
              ref={audio}
              src={audioSrc}
              controls
              css={css({
                width: '100%',
              })}
            />
          </div>
          {useTranscript && (
            <ShowTranscriptBtn id={id} setShowTranscript={setShowTranscript} />
          )}
          <div
            css={css`
              position: absolute;
              display: flex;
              justify-content: center;
              align-items: center;
              left: 0px;
              top: 0px;
              width: 100%;
              height: 100%;
              pointer-events: none;
            `}
          >
            <div
              css={css`
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
       

          width: 100%;
          height: 100%;
          
          box-shadow: ${selected ? `0 0 0 1px ${lPink}` : '0 0 0 1px lightGrey'};
          transition: all 0.2s;
          pointer-events: all;
          cursor: pointer;
          &:hover {
            ${!selected && `box-shadow: 0 0 0 1px ${lPink};`}
          }

          ${selected && 'pointer-events: none;'}
        `}
              onClick={e => {
                e.stopPropagation();
                if (selected) return;
                setCurrentBlock({ id });
              }}
            >

              {!selected && <EditMarker />}
            </div>
          </div>
        </div>
      ) : (
          <div
            css={css`
            position: relative;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 100%;
          `}
          >
            <img src={imgSrc} />
            <audio
              ref={audio}
              src={audioSrc}
              onPlay={() => {
                setIsPlaying(true);
              }}
              onPause={() => {
                audioPausedByUser.current = true;
                setIsPlaying(false);
              }}
              css={css({
                width: '100%',
              })}
              autoPlay={autoPlay && entry && entry.isIntersecting}
              controls
            />
            {useTranscript && (
              <ShowTranscriptBtn id={id} setShowTranscript={setShowTranscript} />
            )}
          </div>
        )}

      {showTranscript && (
        <Transcript
          onClose={() => {
            const tick = setInterval(() => {
              const showTranscriptButtonElement: any = document.querySelector(
                `#show-transcript-${id}`
              );

              if (showTranscriptButtonElement) {
                showTranscriptButtonElement.focus();

                clearInterval(tick);
              }
            }, 0);

            setShowTranscript(false);
          }}
        >
          {transcript}
        </Transcript>
      )}
    </_>
  );
};

export default Audio;
